import { useCallback } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'

import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  FloraTypography
} from '@grupoboticario/flora-react'

import { BackofficeImage } from '@/components/BackofficeImage/BackofficeImage'
import { useUserContext } from '@/context/user'
import { getAppVersion } from '@/utils/envUtils'
import { isBrowser } from '@/utils/isBrowser'
import { theme } from '@/utils/themes/theme'

import { MENU_OPTIONS } from './Menu.constants'
import * as S from './Menu.styles'

const Menu = ({ openMenu, onToggleSideMenu }) => {
  const navigate = useNavigate()
  const { pathname: asPath } = useLocation()
  const { logout, hasPermission } = useUserContext()

  const handleClick = useCallback(
    (e, path) => {
      e.preventDefault()
      if (path) {
        onToggleSideMenu(false)
        navigate(path)
      }
    },
    [navigate, onToggleSideMenu]
  )

  const handleLogoutClick = () => {
    logout()
  }

  return (
    <Box css={S.menuContainerCss}>
      <Flex css={S.getSidebarCss(openMenu)} direction="column">
        <Box css={S.logoHeaderCss}>
          <Box as="button" css={S.closeButtonCss}>
            <Box
              alt="Botão fechar menu"
              as={BackofficeImage}
              height={36}
              onClick={() => onToggleSideMenu(false)}
              src="/img/close.svg"
              width={36}
            />
            Fechar menu
          </Box>
          <Link to="/home">
            <Box css={S.logoVisibilityCss}>
              <Box
                alt="Logo da marca"
                as={BackofficeImage}
                css={S.logoImageCss}
                height={40}
                priority="true"
                src={`/img/logos/${theme(isBrowser() ? globalThis?.location?.hostname : '')}.svg`}
                width={188}
              />
            </Box>
          </Link>
          <Box as="hr" css={S.separatorCss} />
        </Box>
        <Box css={S.scrollableContentCss}>
          {MENU_OPTIONS?.map((item) => {
            const isActive = item.href ? asPath.includes(item.href) : asPath.includes(item.accordion ?? '')

            return (
              <Accordion
                collapsible={true}
                css={S.accordionCss}
                defaultValue={asPath.includes(item.accordion) && item?.accordion}
                key={item.id}
                type="single"
              >
                <AccordionItem
                  onClick={(e) => handleClick(e, item?.href)}
                  value={item?.accordion ? item?.accordion : 'item'}
                >
                  <AccordionButton
                    css={S.getAccordionButtonCss(isActive, item.links.length > 0)}
                    disabled={!hasPermission(item.action)}
                  >
                    <Flex align="flex-start" css={S.getMenuItemFlexCss(hasPermission(item.action), item.icon)}>
                      <FloraTypography css={S.menuItemTextCss} fontSize="bodyLargeStandard">
                        {item.title}
                      </FloraTypography>
                    </Flex>
                  </AccordionButton>
                  {item.links.map((link) => (
                    <AccordionPanel
                      css={S.accordionPanelCss}
                      key={link.href}
                      onClick={(e) => handleClick(e, link.href)}
                    >
                      <Box
                        css={S.getSubMenuItemBoxCss(hasPermission(item?.action))}
                        data-active={asPath.includes(link.href)}
                      >
                        <FloraTypography
                          css={S.getSubMenuItemTextCss(hasPermission(item?.action))}
                          fontSize="bodyLargeShort"
                        >
                          {link.text}
                        </FloraTypography>
                      </Box>
                    </AccordionPanel>
                  ))}
                </AccordionItem>
              </Accordion>
            )
          })}
        </Box>
        <Box as="footer" css={S.footerCss}>
          <Box as="hr" css={S.footerSeparatorCss} />
          <Flex align="flex-start" css={S.footerContentCss} justify="space-around">
            <Box alt="Icon user" as="img" src="/img/userIcon.svg" />
            <Box css={S.footerTextColorCss}>
              <FloraTypography fontSize="exceptionsAuxiliar">Backoffice Fidelidade</FloraTypography>
              <Flex justify="flex-end">
                <FloraTypography fontSize="exceptionsAuxiliar">version {getAppVersion()}</FloraTypography>
              </Flex>
              <Flex css={S.footerLogoutRowCss} justify="flex-end">
                <FloraTypography css={S.logoutTextCss} fontSize="exceptionsAuxiliar" onClick={handleLogoutClick}>
                  Sair
                </FloraTypography>
              </Flex>
            </Box>
          </Flex>
        </Box>
      </Flex>
    </Box>
  )
}

export default Menu
